import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminRecipes.css';
import RecipeDeleteConfirmationModal from './RecipeDeleteConfirmationModal';
import RecipeFormModal from './RecipeFormModal';

// Lambda API endpoint
const API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-recipes-add';
const DELETE_API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-delete-recipes';

function BucksAdminRecipes() {
  // State for recipes list
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  
  // State for form modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState({
    id: null,
    slug: '',
    title: '',
    category: 'customer_recipes',
    product_id: '',
    description: '',
    difficulty: 'Easy',
    prep_time_min: '',
    cook_time_min: '',
    servings: '',
    season: 'Year-round',
    image_s3_key: '',
    ingredients: [],
    steps: [],
    tips: [],
    status: 'draft'
  });
  
  // We still need these refs and states for image handling in the main component
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);
  
  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // State for loading and notifications
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // State for delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recipeId: null,
    recipeTitle: ''
  });
  
  // State for products (for product_id dropdown)
  const [products, setProducts] = useState([]);
  
  // Fetch recipes on component mount
  useEffect(() => {
    fetchRecipes();
    fetchProducts();
  }, []);
  
  // Apply filters when recipes, searchTerm, or filters change
  useEffect(() => {
    applyFilters();
  }, [recipes, searchTerm, categoryFilter, statusFilter]);
  
  // Fetch recipes from the server
  const fetchRecipes = async () => {
    setLoading(true);
    try {
      // API endpoint URL
      const API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-recipes-get';
      
      // Get auth token if needed
      const authToken = sessionStorage.getItem('adminToken');
      
      // Get admin user id if needed
      const adminUserStr = sessionStorage.getItem('adminUser');
      let user_id = null;
      
      if (adminUserStr) {
        try {
          const adminUser = JSON.parse(adminUserStr);
          user_id = adminUser.id;
        } catch (e) {
          console.warn('Failed to parse admin user data');
        }
      }
      
      // Build query parameters based on filters
      let queryParams = new URLSearchParams();
      
      // Add filters if they're active
      if (categoryFilter !== 'all') {
        queryParams.append('category', categoryFilter);
      }
      
      if (statusFilter !== 'all') {
        queryParams.append('status', statusFilter);
      }
      
      if (searchTerm.trim() !== '') {
        queryParams.append('search', searchTerm.trim());
      }
      
      // Add user_id if authentication is required
      if (user_id) {
        queryParams.append('user_id', user_id);
      }
      
      // Build the request URL with query parameters
      const requestURL = `${API_ENDPOINT}?${queryParams.toString()}`;
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if available
      if (authToken) {
        headers['Authorization'] = authToken;
      }
      
      // Make the API request
      const response = await fetch(requestURL, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch recipes: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch recipes');
      }
      
      // Set the recipes in state
      setRecipes(data.recipes);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      setNotification({
        show: true,
        message: 'Failed to load recipes. Please try again later or contact support.',
        type: 'error'
      });
      
      // Set empty recipes array
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch products for product_id dropdown
  const fetchProducts = async () => {
    try {
      // For now, we'll use a mock product list since the /api/products endpoint 
      // seems to be returning HTML instead of JSON
      // In production, you should replace this with your actual products API endpoint
      
      // Check if we have a products endpoint in environment or use a fallback
      const productsEndpoint = process.env.REACT_APP_PRODUCTS_API || '/api/products';
      
      console.log(`Attempting to fetch products from: ${productsEndpoint}`);
      
      try {
        // Try the configured endpoint first
        const response = await fetch(productsEndpoint, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': sessionStorage.getItem('adminToken') || ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
          return;
        }
      } catch (apiError) {
        console.warn('Could not fetch products from API, using fallback data:', apiError);
      }
      
      // Fallback to mock data if API request fails
      console.log('Using fallback product data');
      const mockProducts = [
        { id: 1, name: 'Apple Cider' },
        { id: 2, name: 'Maple Syrup' },
        { id: 3, name: 'Honey' },
        { id: 4, name: 'Farm Fresh Eggs' },
        { id: 5, name: 'Berry Jam' }
      ];
      
      setProducts(mockProducts);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setNotification({
        show: true,
        message: 'Failed to load products. Some features may be limited.',
        type: 'warning'
      });
      
      // Set a minimal fallback product list
      setProducts([
        { id: 1, name: 'Apple Cider' },
        { id: 2, name: 'Maple Syrup' }
      ]);
    }
  };
  
  // Apply filters to recipes
  const applyFilters = () => {
    let filtered = [...recipes];
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(recipe => recipe.category === categoryFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(recipe => recipe.status === statusFilter);
    }
    
    // Apply search term
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(recipe => 
        recipe.title.toLowerCase().includes(search) || 
        recipe.description.toLowerCase().includes(search) ||
        recipe.slug.toLowerCase().includes(search)
      );
    }
    
    setFilteredRecipes(filtered);
  };
  
  // Format time for display
  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? mins + 'm' : ''}`;
    } else {
      return `${mins}m`;
    }
  };
  
  // Get total time
  const getTotalTime = (recipe) => {
    const prep = parseInt(recipe.prep_time_min) || 0;
    const cook = parseInt(recipe.cook_time_min) || 0;
    return formatTime(prep + cook);
  };
  
  // Create slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };
  
  // Debug logger function
  const logDebugInfo = (data) => {
    console.log('DEBUG: Recipe data being sent to API:', {
      id: data.recipe.id,
      title: data.recipe.title,
      season: data.recipe.season,
      // Log the character codes of the season string to identify special characters
      seasonChars: Array.from(data.recipe.season).map(c => c.charCodeAt(0)),
      // Add a direct check for the season value
      isYearRound: data.recipe.season === 'Year-round',
      isYearRoundWithSpecialHyphen: data.recipe.season === 'Year‐round'
    });
  };
  
  // Modified functions to work with the Lambda API
  
  // Handle opening the form for a new recipe
  const handleAddRecipe = () => {
    setCurrentRecipe({
      id: null,
      slug: '',
      title: '',
      category: 'customer_recipes',
      product_id: '',
      description: '',
      difficulty: 'Easy',
      prep_time_min: '',
      cook_time_min: '',
      servings: '',
      season: 'Year-round',
      image_s3_key: '',
      ingredients: [],
      steps: [],
      tips: [],
      status: 'draft'
    });
    setImageFile(null);
    setImagePreview('');
    setEditMode(false);
    setIsModalOpen(true);
  };
  
  // Handle opening the form for editing a recipe
  const handleEditRecipe = (recipe) => {
    // Ensure the season value is normalized
    let normalizedRecipe = {
      ...recipe,
      product_id: recipe.product_id ? recipe.product_id.toString() : '',
      // Normalize the season value to use standard hyphen
      season: recipe.season && recipe.season.toLowerCase().startsWith('year') ? 'Year-round' : recipe.season
    };
    
    setCurrentRecipe(normalizedRecipe);
    setImagePreview(recipe.image_s3_key ? `https://bucks-farmstand-product-images.s3.amazonaws.com/${recipe.image_s3_key}` : '');
    setEditMode(true);
    setIsModalOpen(true);
  };
  
  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  // Convert image file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file || !(file instanceof Blob)) {
        reject(new Error('Invalid file provided'));
        return;
      }
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error('Error converting file to base64:', error);
        reject(new Error('Failed to convert image to base64'));
      };
    });
  };
  
  // Handle saving a recipe (new or edited) using the Lambda function
  const handleSaveRecipe = async (updatedRecipe, imageFile) => {
    try {
      // Set loading state
      setLoading(true);
      
      // Generate slug if not provided
      if (!updatedRecipe.slug) {
        updatedRecipe.slug = generateSlug(updatedRecipe.title);
      }
      
      // Normalize season value to use standard hyphen
      if (updatedRecipe.season && updatedRecipe.season.toLowerCase().startsWith('year')) {
        updatedRecipe.season = 'Year-round';
      }
      
      // Get admin user from sessionStorage
      const adminUserStr = sessionStorage.getItem('adminUser');
      if (!adminUserStr) {
        throw new Error('Authentication required. Please log in and try again.');
      }
      
      // Parse admin user data
      let adminUser;
      try {
        adminUser = JSON.parse(adminUserStr);
      } catch (e) {
        throw new Error('Invalid user data. Please log in again.');
      }
      
      if (!adminUser || !adminUser.id) {
        throw new Error('User data is incomplete. Please log in again.');
      }
      
      // Prepare the request data with proper product_id handling
      const requestData = {
        recipe: {
          ...updatedRecipe,
          // Ensure product_id is null if empty string or 0
          product_id: (!updatedRecipe.product_id || updatedRecipe.product_id === '' || updatedRecipe.product_id === '0') 
            ? null 
            : updatedRecipe.product_id
        },
        user_id: adminUser.id,
        image_data: null
      };
      
      // If image file is provided, convert it to base64
      if (imageFile) {
        try {
          const base64Image = await fileToBase64(imageFile);
          requestData.image_data = base64Image;
        } catch (imageError) {
          console.error('Error processing image:', imageError);
          throw new Error(`Failed to process image: ${imageError.message}`);
        }
      }
      
      // Get the auth token from sessionStorage
      const authToken = sessionStorage.getItem('adminToken');
      if (!authToken) {
        throw new Error('Authentication token not available. Please log in and try again.');
      }
      
      // Add debug logging
      logDebugInfo(requestData);
      
      // Determine which API endpoint to use based on whether we're editing or creating
      let API_ENDPOINT;
      if (editMode) {
        // Use the edit recipe endpoint
        API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-edit-recipes';
      } else {
        // Use the add recipe endpoint
        API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-recipes-add';
      }
      
      // Make the API request
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify(requestData)
      });
      
      // Check if the request was successful
      if (!response.ok) {
        let errorMessage = `Failed to ${editMode ? 'update' : 'save'} recipe (Status ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMessage += `: ${errorData.errors.join(', ')}`;
          }
        } catch (e) {
          // If we can't parse the error JSON, just use the status text
          errorMessage = `Failed to ${editMode ? 'update' : 'save'} recipe: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Parse the response
      const responseData = await response.json();
      
      // Update UI based on response
      if (responseData.success) {
        // If it was an update, update the recipe in the list
        if (editMode) {
          // Check if the response includes the full updated recipe or just metadata
          if (responseData.recipe) {
            // If full recipe is returned, use it directly
            const updatedRecipes = recipes.map(recipe => 
              recipe.id === updatedRecipe.id ? responseData.recipe : recipe
            );
            setRecipes(updatedRecipes);
          } else {
            // If only metadata is returned, update with what we know
            const updatedRecipes = recipes.map(recipe => 
              recipe.id === updatedRecipe.id ? {
                ...updatedRecipe,
                image_s3_key: responseData.image_s3_key || updatedRecipe.image_s3_key,
                updated_at: new Date().toISOString().split('T')[0]
              } : recipe
            );
            setRecipes(updatedRecipes);
          }
          
          setNotification({
            show: true,
            message: `Recipe "${updatedRecipe.title}" has been updated`,
            type: 'success'
          });
          
          // Refresh the recipes list to get the latest data
          setTimeout(() => {
            fetchRecipes();
          }, 500);
        } else {
          // If it was a new recipe, add it to the list with the returned ID
          const newRecipe = {
            ...updatedRecipe,
            id: responseData.recipe_id || responseData.recipe?.id,
            image_s3_key: responseData.image_s3_key || responseData.recipe?.image_s3_key || '',
            created_at: new Date().toISOString().split('T')[0],
            updated_at: new Date().toISOString().split('T')[0]
          };
          
          setRecipes([...recipes, newRecipe]);
          
          setNotification({
            show: true,
            message: `Recipe "${updatedRecipe.title}" has been created`,
            type: 'success'
          });
        }
        
        // Close the modal
        setIsModalOpen(false);
      } else {
        throw new Error(responseData.message || `Failed to ${editMode ? 'update' : 'save'} recipe`);
      }
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'saving'} recipe:`, error);
      setNotification({
        show: true,
        message: `Failed to ${editMode ? 'update' : 'save'} recipe: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening delete confirmation modal
  const handleOpenDeleteModal = (id, title) => {
    setDeleteModal({
      isOpen: true,
      recipeId: id,
      recipeTitle: title
    });
  };
  
  // Handle closing delete confirmation modal
  const handleCloseDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      recipeId: null,
      recipeTitle: ''
    });
  };
  
  // Handle confirming deletion - UPDATED TO USE LAMBDA API
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      
      // Make the API request to delete the recipe using Lambda endpoint
      const response = await fetch(`${DELETE_API_ENDPOINT}?id=${deleteModal.recipeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check if the request was successful
      if (!response.ok) {
        let errorMessage = `Failed to delete recipe (Status ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse the error JSON, just use the status text
          errorMessage = `Failed to delete recipe: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Parse the response
      const responseData = await response.json();
      
      if (responseData.success) {
        // Update UI after successful deletion
        const updatedRecipes = recipes.filter(recipe => recipe.id !== deleteModal.recipeId);
        setRecipes(updatedRecipes);
        
        setNotification({
          show: true,
          message: `Recipe "${deleteModal.recipeTitle}" has been deleted`,
          type: 'success'
        });
      } else {
        // Handle API success: false response
        throw new Error(responseData.message || 'Failed to delete recipe');
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      setNotification({
        show: true,
        message: `Failed to delete recipe: ${error.message}`,
        type: 'error'
      });
    } finally {
      handleCloseDeleteModal();
      setLoading(false);
    }
  };
  
  // Close notification
  const closeNotification = () => {
    setNotification({ show: false, message: '', type: '' });
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'published':
        return 'status-published';
      case 'draft':
        return 'status-draft';
      case 'archived':
        return 'status-archived';
      default:
        return '';
    }
  };
  
  // Get category badge class
  const getCategoryBadgeClass = (category) => {
    switch (category) {
      case 'bucks_products':
        return 'category-bucks';
      case 'customer_recipes':
        return 'category-customer';
      default:
        return '';
    }
  };
  
  return (
    <div className="recipes-admin-section">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="close-notification" 
            onClick={closeNotification}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Action Bar */}
      <div className="recipes-actions">
        <div className="search-filter-container">
          {/* Search */}
          <div className="search-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              className="search-input"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Category Filter */}
          <div className="filter-container">
            <span className="filter-label">Category:</span>
            <select 
              className="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="bucks_products">Buck's Products</option>
              <option value="customer_recipes">Customer Recipes</option>
            </select>
          </div>
          
          {/* Status Filter */}
          <div className="filter-container">
            <span className="filter-label">Status:</span>
            <select 
              className="category-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        
        {/* Add Recipe Button */}
        <button 
          className="add-recipe-btn"
          onClick={handleAddRecipe}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
          Add Recipe
        </button>
      </div>
      
      {/* Recipe Form Modal */}
      <RecipeFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveRecipe}
        editMode={editMode}
        initialRecipe={currentRecipe}
        products={products}
      />
      
      {/* Recipes Table */}
      <div className="recipes-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading recipes...</p>
          </div>
        ) : filteredRecipes.length > 0 ? (
          <table className="recipes-table">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Recipe</th>
                <th>Category</th>
                <th>Time</th>
                <th>Season</th>
                <th>Servings</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipes.map(recipe => (
                <tr key={recipe.id}>
                  <td>
                    <div className="recipe-info">
                      {recipe.image_s3_key && (
                        <div className="recipe-thumbnail">
                          <img 
                            src={`https://bucks-farmstand-product-images.s3.amazonaws.com/${recipe.image_s3_key}`}
                            alt={recipe.title} 
                            onError={(e) => {
                              // Fallback to placeholder image if S3 image fails to load
                              e.target.src = `https://images.unsplash.com/photo-${recipe.image_s3_key.includes('apple-crisp') ? '1568571780765-9276a36201a8' : 
                                        recipe.image_s3_key.includes('apple-sauce') ? '1579697096985-41fe1430e5df' : 
                                        '1553530666-ba11a90a0805'}`;
                            }}
                          />
                        </div>
                      )}
                      <div className="recipe-details">
                        <div className="recipe-name">{recipe.title}</div>
                        <div className="recipe-slug">/{recipe.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`category-badge ${getCategoryBadgeClass(recipe.category)}`}>
                      {recipe.category === 'bucks_products' ? 'Buck\'s Products' : 'Customer Recipes'}
                    </span>
                  </td>
                  <td>{getTotalTime(recipe)}</td>
                  <td>{recipe.season}</td>
                  <td>{recipe.servings}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(recipe.status)}`}>
                      {recipe.status.charAt(0).toUpperCase() + recipe.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditRecipe(recipe)}
                        title="Edit recipe"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleOpenDeleteModal(recipe.id, recipe.title)}
                        title="Delete recipe"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-results">
            <p>No recipes found matching your search criteria</p>
            <button 
              className="reset-search-btn"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setStatusFilter('all');
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <RecipeDeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        recipeTitle={deleteModal.recipeTitle}
      />
    </div>
  );
}

export default BucksAdminRecipes;